import React, { useState, useMemo } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import {
  ChevronUpDownIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// =============================================================================
// CONFIG - PennDOT Publication 408/2020 Section 404
// =============================================================================

const LOT_LENGTH = 528; // feet (0.1 mile)
const BUMP_THRESHOLD = 0.1875; // 3/16 inch - bumps exceeding this make lot defective

const SCHEDULES = {
  A: {
    id: 'A',
    label: 'Schedule A',
    description: '3-Operation Expressway',
    tiers: [
      { max: 35,       incentive: 600, tier: 'bonus',      label: '< 35' },
      { max: 50,       incentive: 300, tier: 'bonus',      label: '35 – 50' },
      { max: 60,       incentive: 150, tier: 'bonus',      label: '50 – 60' },
      { max: 70,       incentive: 0,   tier: 'base',       label: '60 – 70' },
      { max: Infinity, incentive: 0,   tier: 'corrective', label: '> 70', requiresCorrection: true },
    ],
    correctionThreshold: 70,
  },
  B: {
    id: 'B',
    label: 'Schedule B',
    description: '2-Op Expressway / Non-Expressway',
    tiers: [
      { max: 45,       incentive: 600, tier: 'bonus',      label: '< 45' },
      { max: 55,       incentive: 300, tier: 'bonus',      label: '45 – 55' },
      { max: 70,       incentive: 150, tier: 'bonus',      label: '55 – 70' },
      { max: 90,       incentive: 0,   tier: 'base',       label: '70 – 90' },
      { max: Infinity, incentive: 0,   tier: 'corrective', label: '> 90', requiresCorrection: true },
    ],
    correctionThreshold: 90,
  },
};

const DEFAULT_SETTINGS = {
  schedule: 'A',
  excluded: false,
};

// =============================================================================
// CALCULATION HELPERS
// =============================================================================

const getIncentiveTier = (iri, schedule) => {
  const sched = SCHEDULES[schedule];
  for (const tier of sched.tiers) {
    if (iri < tier.max || (tier.max === Infinity && iri >= sched.correctionThreshold)) {
      if (tier.max === Infinity) {
        return {
          incentive: 0,
          tier: tier.tier,
          label: tier.label,
          requiresCorrection: true,
        };
      }
      return {
        incentive: tier.incentive,
        tier: tier.tier,
        label: tier.label,
        requiresCorrection: false,
      };
    }
  }
  // Fallback - should not reach here
  const lastTier = sched.tiers[sched.tiers.length - 1];
  return {
    incentive: 0,
    tier: lastTier.tier,
    label: lastTier.label,
    requiresCorrection: true,
  };
};

const getBumpsInSegment = (segmentStart, segmentEnd, surfaceEvents) => {
  const minSta = Math.min(segmentStart, segmentEnd);
  const maxSta = Math.max(segmentStart, segmentEnd);
  
  return (surfaceEvents || []).filter(event => {
    const eventStation = event.distance || event.peakStation || 0;
    const isBump = event.eventType === 'Bump';
    const exceedsThreshold = (event.magnitude || 0) > BUMP_THRESHOLD;
    const inRange = eventStation >= minSta && eventStation <= maxSta;
    return isBump && exceedsThreshold && inRange;
  });
};

// =============================================================================
// MAIN CALCULATION
// =============================================================================

const calculate = (segments, surfaceEvents, settings, isReversed) => {
  if (!segments?.length) return null;
  
  const validSegments = segments
    .filter(seg => !seg.exception)
    .map((seg) => ({
      ...seg,
      startStation: seg.from_station_ft || 0,
      stopStation: seg.to_station_ft || 0,
    }));
  
  if (validSegments.length === 0) return null;
  
  const sortedSegments = [...validSegments].sort((a, b) => 
    isReversed 
      ? b.startStation - a.startStation
      : a.startStation - b.startStation
  );
  
  let totalIncentive = 0;
  let totalDistance = 0;
  let iriSum = 0;
  let correctiveIRICount = 0;
  let correctiveBumpCount = 0;
  
  // Tier counts
  const tierCounts = {
    600: 0,
    300: 0,
    150: 0,
    0: 0,
  };
  
  const rows = sortedSegments.map((seg, i) => {
    const iri = seg.iri_avg_in_per_mi || 0;
    const segmentLength = Math.abs(seg.stopStation - seg.startStation);
    const prorationFactor = Math.min(segmentLength / LOT_LENGTH, 1.0);
    const isPartialLot = segmentLength < LOT_LENGTH;
    
    // Get IRI-based incentive tier
    const tierResult = getIncentiveTier(iri, settings.schedule);
    
    // Check for bump defects in this segment
    const bumpsInSegment = getBumpsInSegment(seg.startStation, seg.stopStation, surfaceEvents);
    const hasBumpDefect = bumpsInSegment.length > 0;
    
    // Determine if lot is defective
    const isDefectiveIRI = tierResult.requiresCorrection;
    const isDefective = isDefectiveIRI || hasBumpDefect;
    
    // Calculate prorated incentive (only if not defective)
    let proratedIncentive = 0;
    if (!isDefective) {
      proratedIncentive = tierResult.incentive * prorationFactor;
      totalIncentive += proratedIncentive;
      
      // Count tiers (count partial lots as fractional)
      if (tierResult.incentive in tierCounts) {
        tierCounts[tierResult.incentive] += prorationFactor;
      }
    }
    
    // Track defective counts
    if (isDefectiveIRI) correctiveIRICount++;
    if (hasBumpDefect && !isDefectiveIRI) correctiveBumpCount++; // Only count bump if not already IRI defective
    
    totalDistance += segmentLength;
    iriSum += iri;
    
    const displayStart = isReversed ? seg.stopStation : seg.startStation;
    const displayStop = isReversed ? seg.startStation : seg.stopStation;
    
    return {
      num: i + 1,
      segmentId: seg.segment_id,
      iri,
      startStation: displayStart,
      stopStation: displayStop,
      length: segmentLength,
      prorationFactor,
      isPartialLot,
      baseIncentive: tierResult.incentive,
      proratedIncentive,
      tierLabel: tierResult.label,
      tier: tierResult.tier,
      isDefective,
      isDefectiveIRI,
      hasBumpDefect,
      bumpCount: bumpsInSegment.length,
    };
  });
  
  const avgIRI = rows.length > 0 ? iriSum / rows.length : 0;
  const totalDefectiveCount = correctiveIRICount + correctiveBumpCount;
  
  return {
    rows,
    totalIncentive,
    avgIRI,
    totalDistance,
    segmentCount: rows.length,
    correctiveIRICount,
    correctiveBumpCount,
    totalDefectiveCount,
    tierCounts,
  };
};

// =============================================================================
// HELPERS
// =============================================================================

const cn = (...c) => c.filter(Boolean).join(' ');

const fmtStation = (ft) => {
  if (ft == null) return '—';
  const hundreds = Math.floor(ft / 100);
  const remainder = Math.round(ft % 100);
  return `${hundreds}+${String(remainder).padStart(2, '0')}`;
};

const fmtCurrency = (v) => {
  const formatted = Math.abs(v).toLocaleString('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    minimumFractionDigits: 2 
  });
  return v < 0 ? `-${formatted}` : formatted;
};

// =============================================================================
// COPY VALUE COMPONENT
// =============================================================================

const CopyValue = ({ label, value, displayValue, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <div className={cn('flex items-center justify-between py-1.5', className)}>
      <span className="text-gray-600 text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold text-gray-900">{displayValue ?? value}</span>
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-gray-100 transition-colors"
          title="Copy value"
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// COPYABLE CELL COMPONENT
// =============================================================================

const CopyableCell = ({ value, displayValue, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <td className={cn('px-3 py-2 group', className)}>
      <div className="flex items-center gap-1">
        <span>{displayValue ?? value}</span>
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
          title="Copy value"
        >
          {copied ? (
            <CheckIcon className="h-3 w-3 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
    </td>
  );
};

// =============================================================================
// COPYABLE COLUMN HEADER
// =============================================================================

const CopyableHeader = ({ label, data, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(data.join('\r\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <th className={cn('px-3 py-2 text-left group', className)}>
      <span>{label}</span>
      <button
        onClick={handleCopy}
        className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
        title={`Copy ${label} column`}
      >
        {copied ? (
          <CheckIcon className="h-3.5 w-3.5 text-green-500 inline" />
        ) : (
          <ClipboardDocumentIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 inline" />
        )}
      </button>
    </th>
  );
};

// =============================================================================
// PAY TABLE MODAL
// =============================================================================

const PayTableModal = ({ open, onClose, schedule }) => {
  const sched = SCHEDULES[schedule];
  
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <DialogPanel className="bg-white rounded shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
            <DialogTitle className="text-base font-semibold text-gray-900">
              PennDOT Pay Adjustment Table
            </DialogTitle>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <div className="p-4 space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <div className="font-semibold text-blue-800 mb-1">Current Configuration</div>
              <div className="text-blue-700">
                {sched.label} — {sched.description}
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Section 404.4 Table A — Incentive per 0.1 Mile Lot
              </h3>
              <table className="w-full text-sm border border-gray-200">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">IRI (in/mi)</th>
                    <th className="px-3 py-2 text-left border-b">Incentive</th>
                    <th className="px-3 py-2 text-left border-b">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sched.tiers.map((tier, i) => (
                    <tr key={i} className={tier.requiresCorrection ? 'bg-red-50' : ''}>
                      <td className="px-3 py-2 text-gray-700">{tier.label}</td>
                      <td className={cn(
                        'px-3 py-2 font-mono',
                        tier.requiresCorrection ? 'text-red-700' : 
                        tier.incentive > 0 ? 'text-green-700' : 'text-gray-500'
                      )}>
                        {tier.requiresCorrection ? '—' : fmtCurrency(tier.incentive)}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {tier.requiresCorrection ? (
                          <span className="text-red-600 font-semibold">Corrective Action Required</span>
                        ) : tier.incentive === 0 ? (
                          'Full pay, no incentive'
                        ) : (
                          'Incentive'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-yellow-800 mb-1">Bump Defects</div>
                  <div className="text-yellow-700 text-xs">
                    Any lot with individual bumps exceeding 3/16" (tested with 10-foot straightedge) 
                    is defective regardless of IRI and requires corrective action.
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 rounded text-xs text-gray-600 space-y-1">
              <p className="font-semibold">Reference</p>
              <p>PennDOT Publication 408/2020, Section 404</p>
              <p>Evaluation of Asphalt Pavement Ride Quality and Payment of Incentive</p>
              <p className="mt-2">
                <strong>Schedule A:</strong> Expressway work using three operations (leveling, intermediate, surface)
              </p>
              <p>
                <strong>Schedule B:</strong> Expressway work using two operations, or non-expressway work using two or more operations
              </p>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function PennsylvaniaPayAdjustment({ project, segments, surfaceEvents, isReversed = false }) {
  const [settings, setSettings] = useState(() => ({ 
    ...DEFAULT_SETTINGS, 
    ...project?.payAdjustment,
  }));
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [showPayTable, setShowPayTable] = useState(false);
  
  const data = useMemo(
    () => calculate(segments, surfaceEvents, settings, isReversed), 
    [segments, surfaceEvents, settings, isReversed]
  );
  
  const displayRows = useMemo(() => {
    if (!data) return [];
    return issuesOnly ? data.rows.filter(r => r.isDefective) : data.rows;
  }, [data, issuesOnly]);
  
  const sched = SCHEDULES[settings.schedule];
  
  const exportCSV = () => {
    const headers = [
      'Lot',
      'Beginning Sta.',
      'Ending Sta.',
      'Length (ft)',
      'IRI (in/mi)',
      'Base Incentive',
      'Prorated Incentive',
      'Defective',
      'Defect Reason'
    ].join(',');
    
    const csvRows = displayRows.map(r => [
      r.num,
      r.startStation.toFixed(0),
      r.stopStation.toFixed(0),
      r.length.toFixed(0),
      r.iri.toFixed(1),
      r.isDefective ? '' : r.baseIncentive.toFixed(2),
      r.isDefective ? '' : r.proratedIncentive.toFixed(2),
      r.isDefective ? 'Yes' : 'No',
      r.isDefectiveIRI ? 'IRI' : r.hasBumpDefect ? 'Bump' : '',
    ].join(','));
    
    const blob = new Blob([[headers, ...csvRows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `penndot-404-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  
  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      <PayTableModal open={showPayTable} onClose={() => setShowPayTable(false)} schedule={settings.schedule} />
      
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <div className="mr-2">
          <h3 className="text-sm font-semibold text-gray-900">Profile Summary Sheet</h3>
          <p className="text-xs text-gray-500">PennDOT Section 404</p>
        </div>
        
        <div className="h-8 w-px bg-gray-300" />
        
        {/* Schedule Selector */}
        <Listbox value={settings.schedule} onChange={(v) => setSettings(s => ({ ...s, schedule: v }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left whitespace-nowrap">
              {sched.label}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
              {Object.values(SCHEDULES).map(s => (
                <ListboxOption 
                  key={s.id} 
                  value={s.id} 
                  className={({ active }) => cn('px-3 py-2 cursor-pointer whitespace-nowrap', active && 'bg-blue-50')}
                >
                  <span className="font-medium">{s.label}</span>
                  <span className="text-gray-500 ml-2 text-xs">{s.description}</span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        
        <div className="flex-1" />
        
        <button 
          onClick={() => setShowPayTable(true)} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
        >
          <InformationCircleIcon className="h-4 w-4" />
          Pay Table
        </button>
        
        <button 
          onClick={exportCSV} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export
        </button>
      </div>
      
      {/* Summary */}
      {data ? (
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="grid grid-cols-2 gap-6">
            {/* Left: Incentive Breakdown */}
            <div className="border border-gray-200 rounded">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700">Incentive Breakdown</h4>
              </div>
              <div className="px-3 py-2 divide-y divide-gray-100">
                <CopyValue 
                  label="$600 Lots"
                  value={data.tierCounts[600].toFixed(2)}
                  displayValue={data.tierCounts[600] % 1 === 0 ? data.tierCounts[600].toFixed(0) : data.tierCounts[600].toFixed(2)}
                />
                <CopyValue 
                  label="$300 Lots"
                  value={data.tierCounts[300].toFixed(2)}
                  displayValue={data.tierCounts[300] % 1 === 0 ? data.tierCounts[300].toFixed(0) : data.tierCounts[300].toFixed(2)}
                />
                <CopyValue 
                  label="$150 Lots"
                  value={data.tierCounts[150].toFixed(2)}
                  displayValue={data.tierCounts[150] % 1 === 0 ? data.tierCounts[150].toFixed(0) : data.tierCounts[150].toFixed(2)}
                />
                <CopyValue 
                  label="$0 Lots"
                  value={data.tierCounts[0].toFixed(2)}
                  displayValue={data.tierCounts[0] % 1 === 0 ? data.tierCounts[0].toFixed(0) : data.tierCounts[0].toFixed(2)}
                />
              </div>
            </div>
            
            {/* Right: Totals */}
            <div className="border border-gray-200 rounded">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-700">Totals</h4>
                {data.totalDefectiveCount > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded">
                    Correction Required
                  </span>
                )}
              </div>
              <div className="px-3 py-2 divide-y divide-gray-100">
                <CopyValue 
                  label="Total Incentive"
                  value={data.totalIncentive.toFixed(2)}
                  displayValue={fmtCurrency(data.totalIncentive)}
                />
                <CopyValue 
                  label="Average IRI"
                  value={data.avgIRI.toFixed(1)}
                  displayValue={`${data.avgIRI.toFixed(1)} in/mi`}
                />
                {data.correctiveIRICount > 0 && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-gray-600 text-sm">Defective Lots (IRI)</span>
                    <span className="font-mono text-sm font-semibold text-red-600">{data.correctiveIRICount}</span>
                  </div>
                )}
                {data.correctiveBumpCount > 0 && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-gray-600 text-sm">Defective Lots (Bump)</span>
                    <span className="font-mono text-sm font-semibold text-red-600">{data.correctiveBumpCount}</span>
                  </div>
                )}
                <CopyValue 
                  label="Total Distance"
                  value={data.totalDistance.toFixed(0)}
                  displayValue={`${data.totalDistance.toFixed(0)} ft`}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-gray-400 border-b border-gray-200">
          No segment data available
        </div>
      )}
      
      {/* Segments Header */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Lots {data && <span className="text-gray-400 font-normal">({data.rows.length})</span>}
        </span>
        {data && data.totalDefectiveCount > 0 && (
          <label className="flex items-center gap-1.5 text-gray-500 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={issuesOnly} 
              onChange={e => setIssuesOnly(e.target.checked)} 
              className="w-3.5 h-3.5 text-blue-600 rounded" 
            />
            Show defective only
          </label>
        )}
      </div>
      
      {/* Table */}
      <div className="flex-1 overflow-auto">
        {displayRows.length > 0 ? (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left w-12">Lot</th>
                <th className="px-3 py-2 text-left">Beginning Sta.</th>
                <th className="px-3 py-2 text-left">Ending Sta.</th>
                <th className="px-3 py-2 text-left">Length (ft)</th>
                <CopyableHeader label="IRI (in/mi)" data={displayRows.map(r => r.iri.toFixed(1))} />
                <CopyableHeader 
                  label="Lot Incentive" 
                  data={displayRows.map(r => r.isDefective ? '' : r.proratedIncentive.toFixed(2))} 
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayRows.map(r => (
                <tr key={r.num} className={cn('hover:bg-gray-50', r.isDefective && 'bg-red-50')}>
                  <td className="px-3 py-2 text-gray-400 text-xs">{r.num}</td>
                  <CopyableCell 
                    value={r.startStation.toFixed(0)} 
                    displayValue={fmtStation(r.startStation)}
                    className="text-gray-700 font-mono text-xs"
                  />
                  <CopyableCell 
                    value={r.stopStation.toFixed(0)} 
                    displayValue={fmtStation(r.stopStation)}
                    className="text-gray-700 font-mono text-xs"
                  />
                  <CopyableCell 
                    value={r.length.toFixed(0)} 
                    displayValue={r.length.toFixed(0)}
                    className="text-gray-700"
                  />
                  <td className={cn(
                    'px-3 py-2 text-left',
                    r.isDefectiveIRI ? 'text-red-600 font-semibold' : 'text-gray-900'
                  )}>
                    {r.iri.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 text-left">
                    {r.isDefective ? (
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded">
                          {r.isDefectiveIRI ? 'IRI' : 'Bump'}
                        </span>
                        {r.hasBumpDefect && (
                          <span className="text-xs text-red-600">
                            {r.bumpCount} bump{r.bumpCount > 1 ? 's' : ''} &gt;3/16"
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          'font-mono',
                          r.proratedIncentive > 0 ? 'text-green-600' : 'text-gray-500'
                        )}>
                          {fmtCurrency(r.proratedIncentive)}
                        </span>
                        {r.isPartialLot && (
                          <span className="text-xs text-gray-400 italic">
                            (prorated)
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
              <tr>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-xs text-gray-500 text-right">Total:</td>
                <td className="px-3 py-2 font-semibold text-gray-900">{data.totalDistance.toFixed(0)}</td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  <span className="mr-2">Avg:</span>
                  <span className="font-semibold text-gray-900">{data.avgIRI.toFixed(1)}</span>
                </td>
                <td className="px-3 py-2">
                  <span className="font-mono font-semibold text-green-600">
                    {fmtCurrency(data.totalIncentive)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 py-12">
            <p>{issuesOnly ? 'No defective lots' : 'No segment data available'}</p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span>PennDOT Publication 408/2020, Section 404</span>
          {isReversed && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Reverse Stationing</span>
          )}
        </div>
        <span>
          {sched.label} — Correction threshold: &gt;{sched.correctionThreshold} in/mi
        </span>
      </div>
    </div>
  );
}